import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import SearchBar from "../components/SearchBar";
import { FaClock, FaMapMarkerAlt } from "react-icons/fa";
import "../styles/ModernPages.css";
import usePageMeta from "../hooks/usePageMeta";
import { pageTitle, breadcrumbSchema } from "../lib/seo";

function PackagesNew() {
  usePageMeta({
    title: pageTitle("Holiday Packages – International & Domestic Tours"),
    description: "Explore international and domestic holiday packages from India — Bali, Singapore, Thailand, Vietnam, Europe, Kashmir, Kerala, Andaman and more. Compare itineraries, inclusions and prices per person.",
    jsonLd: breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Holiday Packages", path: "/packages" }]),
  });
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("featured");

  useEffect(() => {
    fetchPackages();
  }, []);

  async function fetchPackages() {
    try {
      const { data } = await supabase
        .from("packages")
        .select("*")
        .order("created_at", { ascending: false });
      setPackages(data || []);
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  }

  const filteredPackages = useMemo(() => {
    let result = [...packages];

    // Search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name?.toLowerCase().includes(query) ||
          p.city?.toLowerCase().includes(query) ||
          p.country?.toLowerCase().includes(query)
      );
    }

    // Category
    if (categoryFilter !== "all") {
      result = result.filter((p) => p.category === categoryFilter);
    }

    // Sort
    if (sortBy === "priceLow") {
      result.sort((a, b) => a.price - b.price);
    } else if (sortBy === "priceHigh") {
      result.sort((a, b) => b.price - a.price);
    } else if (sortBy === "duration") {
      result.sort((a, b) => a.duration_nights - b.duration_nights);
    }

    return result;
  }, [packages, searchQuery, categoryFilter, sortBy]);

  function formatPrice(price) {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  }

  return (
    <div className="packages-new-page">
      {/* Hero */}
      <div className="page-hero">
        <h1>Discover Amazing Packages</h1>
        <p>
          Explore handpicked travel packages with the best prices and unforgettable experiences
        </p>
      </div>

      {/* Search */}
      <div className="search-section">
        <div className="search-container">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search destinations, cities, countries..."
          />
        </div>
      </div>

      {/* Content */}
      <div className="content-container">
        {/* Filters */}
        <div className="filters-bar">
          <select
            className="filter-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All Destinations</option>
            <option value="domestic">Domestic</option>
            <option value="international">International</option>
          </select>

          <select
            className="filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="featured">Featured</option>
            <option value="priceLow">Price: Low to High</option>
            <option value="priceHigh">Price: High to Low</option>
            <option value="duration">Duration</option>
          </select>
        </div>

        {/* Loading */}
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredPackages.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">✈</div>
            <h3>No packages found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        )}

        {/* Packages Grid */}
        {!loading && filteredPackages.length > 0 && (
          <>
            <div className="cards-grid">
              {filteredPackages.map((pkg) => (
                <Link
                  key={pkg.id}
                  className="modern-card"
                  to={`/packages/${pkg.id}/`}
                >
                  <div className="card-image">
                    <img
                      src={pkg.image || "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=600"}
                      alt={pkg.name}
                      loading="lazy"
                    />
                    <div className="card-badge">
                      {pkg.category === "international" ? "International" : "Domestic"}
                    </div>
                  </div>

                  <div className="card-content">
                    <div className="card-location">
                      <FaMapMarkerAlt style={{ display: "inline", marginRight: 6 }} />
                      {pkg.city}, {pkg.country}
                    </div>

                    <h3 className="card-title">{pkg.name}</h3>

                    <div className="card-meta">
                      <div className="meta-item">
                        <FaClock />
                        <span>{pkg.duration || "3N/4D"}</span>
                      </div>
                    </div>

                    <div className="card-footer">
                      <div className="card-price">
                        <div className="price-amount">{formatPrice(pkg.price)}</div>
                        <div className="price-label">per person</div>
                      </div>
                      <span className="card-cta">View Details</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div style={{ textAlign: "center", marginTop: 40, color: "#64748b" }}>
              Showing {filteredPackages.length} package{filteredPackages.length !== 1 ? "s" : ""}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PackagesNew;
